import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { getPostBySlug, getRelatedPosts, type PostSection } from "@/data/posts";
import { getReviewsForPost } from "@/data/reviews";
import { getReviewedProduct } from "@/data/reviewed-products";
import { PostCard } from "@/components/PostCard";
import { MgidAd } from "@/components/MgidAd";
import { formatDate } from "@/lib/format";
import { siteConfig, categorySlug } from "@/config";

export const Route = createFileRoute("/post/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Article not found" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const url = `${siteConfig.domain}/post/${params.slug}`;
    const reviews = getReviewsForPost(post.id);
    const reviewedProduct = getReviewedProduct(post.id);
    // Google's Review rich-result does NOT support @type=Article. Keep the
    // Article schema clean and, only for posts that review a single named
    // product, emit a separate SoftwareApplication with aggregateRating +
    // review so Rich Results Test validates without warnings.
    const authorLd = {
      "@type": "Person",
      name: post.author,
      url: `${siteConfig.domain}/about-us`,
      jobTitle: "Editorial Team",
      worksFor: {
        "@type": "Organization",
        name: siteConfig.siteName,
        url: siteConfig.domain,
      },
    };
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      image: [post.thumbnail],
      datePublished: post.publishDate,
      dateModified: post.publishDate,
      author: authorLd,
      publisher: {
        "@type": "Organization",
        name: siteConfig.siteName,
        logo: {
          "@type": "ImageObject",
          url: `${siteConfig.domain}/favicon.ico`,
        },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      keywords: post.keywords.join(", "),
      articleSection: post.category,
    };
    const softwareLd = reviewedProduct && reviews.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: reviewedProduct.name,
          applicationCategory: reviewedProduct.applicationCategory,
          operatingSystem: reviewedProduct.operatingSystem ?? "Web",
          ...(reviewedProduct.url ? { url: reviewedProduct.url } : {}),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: (
              reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
            ).toFixed(1),
            reviewCount: reviews.length,
            bestRating: 5,
            worstRating: 1,
          },
          review: reviews.map((r) => ({
            "@type": "Review",
            author: { "@type": "Person", name: r.name },
            datePublished: r.date,
            reviewBody: r.comment,
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
          })),
        }
      : null;
    const breadcrumbLd = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.domain },
        {
          "@type": "ListItem",
          position: 2,
          name: post.category,
          item: `${siteConfig.domain}/category/${categorySlug(post.category)}`,
        },
        { "@type": "ListItem", position: 3, name: post.title, item: url },
      ],
    };
    // Aggregate all FAQ sections into a single FAQPage schema
    const faqItems = post.body
      .filter((s): s is Extract<PostSection, { type: "faq" }> => s.type === "faq")
      .flatMap((s) => s.items);
    const faqLd = faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }
      : null;
    return {
      meta: [
        { title: `${post.title} | ${siteConfig.siteName}` },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords.join(", ") },
        { name: "author", content: post.author },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { property: "og:image", content: post.thumbnail },
        { property: "article:published_time", content: post.publishDate },
        { property: "article:author", content: post.author },
        { property: "article:section", content: post.category },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.description },
        { name: "twitter:image", content: post.thumbnail },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(jsonLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbLd) },
        ...(softwareLd ? [{ type: "application/ld+json", children: JSON.stringify(softwareLd) }] : []),
        ...(faqLd ? [{ type: "application/ld+json", children: JSON.stringify(faqLd) }] : []),
      ],
    };
  },
  component: PostPage,
});

function RenderSection({ section, i }: { section: PostSection; i: number }) {
  switch (section.type) {
    case "p":
      return <p>{section.text}</p>;
    case "h2":
      return <h2>{section.text}</h2>;
    case "h3":
      return <h3>{section.text}</h3>;
    case "ul":
      return (
        <ul>
          {section.items.map((it, k) => <li key={k}>{it}</li>)}
        </ul>
      );
    case "quote":
      return (
        <blockquote className="my-6 border-l-4 border-primary bg-primary/5 px-5 py-3 italic text-foreground">
          {section.text}
        </blockquote>
      );
    case "table":
      return (
        <div className="my-6 overflow-x-auto rounded-md border border-border">
          <table>
            <thead>
              <tr>{section.headers.map((h, k) => <th key={k}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {section.rows.map((row, rk) => (
                <tr key={rk}>
                  {row.map((cell, ck) => <td key={ck}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "faq":
      return (
        <div className="my-6 divide-y divide-border rounded-lg border border-border bg-card">
          {section.items.map((f, k) => (
            <details key={k} className="group p-5" open={i === 0 && k === 0}>
              <summary className="cursor-pointer list-none text-base font-semibold text-foreground marker:hidden">
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      );
  }
}

function PostPage() {
  const { post } = Route.useLoaderData();
  const related = getRelatedPosts(post.slug, post.category);
  const reviews = getReviewsForPost(post.id);
  // In-article ad position: right after the first H2.

  

  return (
    <article className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-primary">Home</Link></li>
          <li aria-hidden>/</li>
          <li>
            <Link
              to="/category/$category"
              params={{ category: categorySlug(post.category) }}
              className="hover:text-primary"
            >
              {post.category}
            </Link>
          </li>
          <li aria-hidden>/</li>
          <li className="text-foreground line-clamp-1">{post.title}</li>
        </ol>
      </nav>

      <div className="mx-auto max-w-3xl">
        {/* Ad dạng lưới ngay trên tiêu đề (giống layout tham khảo) */}
        <MgidAd widgetId="2074285" minHeight={320} className="mb-6" />

        <header className="mb-6">
          <h1 className="text-3xl font-bold leading-tight tracking-tight text-foreground md:text-4xl">
            {post.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <img
              src="/admin-avatar.webp"
              alt={`${post.author} avatar`}
              width={28}
              height={28}
              loading="lazy"
              className="h-7 w-7 rounded-full border border-border object-cover"
            />
            <span>
              By <span className="font-medium text-foreground">{post.author}</span> •{" "}
              {formatDate(post.publishDate)}
            </span>
            <Link
              to="/category/$category"
              params={{ category: categorySlug(post.category) }}
              className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary hover:bg-primary/15"
            >
              {post.category}
            </Link>
          </div>
        </header>

        {/* Ad ngay dưới tiêu đề bài viết — vị trí CTR cao nhất */}
        <MgidAd widgetId="2074306" minHeight={300} className="mb-8" />


        <figure className="mb-8 overflow-hidden rounded-lg border border-border">
          <img
            src={post.thumbnail}
            alt={post.title}
            width={1200}
            height={675}
            loading="eager"
            decoding="async"
            {...({ fetchpriority: "high" } as { fetchpriority: "high" })}
            sizes="(min-width: 768px) 768px, 100vw"
            onError={(e) => {
              const t = e.currentTarget;
              if (t.src !== siteConfig.fallbackImage) t.src = siteConfig.fallbackImage;
            }}
            className="h-auto w-full object-cover"
          />
        </figure>

        <div className="prose-article">
          {post.body.map((section: PostSection, i: number) => (
            <div key={i}>
              <RenderSection section={section} i={i} />
            </div>
          ))}
        </div>

        {/* Under-article widget */}
        <MgidAd widgetId="2074309" minHeight={300} className="mt-10" />
      </div>

      {reviews.length > 0 && (
        <section className="mx-auto mt-16 max-w-3xl border-t border-border pt-10">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold tracking-tight text-foreground">
                Reader reviews
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                What US professionals said after reading this article.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex" aria-hidden>
                {Array.from({ length: 5 }).map((_, i) => (
                  <svg key={i} className="h-5 w-5 fill-yellow-400" viewBox="0 0 20 20">
                    <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 15l-5.2 2.8 1-5.9L1.5 7.7l5.9-.9L10 1.5z" />
                  </svg>
                ))}
              </div>
              <span className="text-sm font-semibold text-foreground">
                5.0 · {reviews.length} reviews
              </span>
            </div>
          </div>

          <ul className="space-y-4">
            {reviews.map((r, idx) => (
              <li
                key={idx}
                className="rounded-lg border border-border bg-card p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary"
                      aria-hidden
                    >
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {r.role} · {r.location}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex justify-end" aria-label={`${r.rating} out of 5`}>
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <svg key={i} className="h-4 w-4 fill-yellow-400" viewBox="0 0 20 20">
                          <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 15l-5.2 2.8 1-5.9L1.5 7.7l5.9-.9L10 1.5z" />
                        </svg>
                      ))}
                    </div>
                    <time className="text-xs text-muted-foreground" dateTime={r.date}>
                      {formatDate(r.date)}
                    </time>
                  </div>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-foreground/90">
                  {r.comment}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}


      {related.length > 0 && (
        <section className="mt-16 border-t border-border pt-10">
          <h2 className="mb-6 text-2xl font-bold tracking-tight text-foreground">
            Related articles
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {related.map((p) => <PostCard key={p.id} post={p} />)}
          </div>
        </section>
      )}
    </article>
  );
}
